import { TFunction } from "i18next";

import { errors, NO_VALUE_DISPLAY, TRANSLATION_PARAMS_KEY } from "../constants";
import {
  localizePermissionTranslationParams,
  processDynamicTranslationParams,
} from "../dto";
import { ITranslatableItem, TInfoItem, TTranslationParams } from "../models";
import { isTranslatableItem } from "./error.helpers";
import { decodeHtmlEntities } from "./html-decode.helpers";
import { Log } from "./log.helpers";

export const translateInfoItem = (
  item: TInfoItem,
  translateFunction: TFunction,
): string => {
  if (!item) {
    return NO_VALUE_DISPLAY;
  }

  if (typeof item === "string") {
    return item;
  }

  if (isTranslatableItem(item)) {
    const translationParams: TTranslationParams | undefined =
      item[TRANSLATION_PARAMS_KEY];
    const processedParams =
      translationParams &&
      processDynamicTranslationParams(translationParams, translateFunction);

    return translateFunction(
      item.translationKey,
      localizePermissionTranslationParams(
        item.translationKey,
        processedParams,
        translateFunction,
      ),
    );
  }

  if (item.label) {
    return item.label;
  }

  return String(item);
};

export const translateInfoItemDecoded = (
  item: TInfoItem,
  translateFunction: TFunction,
): string => {
  if (!item) {
    return NO_VALUE_DISPLAY;
  }

  if (typeof item === "string") {
    return decodeHtmlEntities(item);
  }

  if (isTranslatableItem(item)) {
    const translationParams: TTranslationParams | undefined =
      item[TRANSLATION_PARAMS_KEY];
    const processedParams =
      translationParams &&
      processDynamicTranslationParams(translationParams, translateFunction);

    return decodeHtmlEntities(
      translateFunction(
        item.translationKey,
        localizePermissionTranslationParams(
          item.translationKey,
          processedParams,
          translateFunction,
        ),
      ),
    );
  }

  if (item.label) {
    return decodeHtmlEntities(item.label);
  }

  return decodeHtmlEntities(String(item));
};

export const warnAndToastInfoItemsAsDetails = ({
  items,
  tFunction,
  headerTranslationKey,
}: {
  items: TInfoItem[] | undefined;
  tFunction: TFunction;
  headerTranslationKey: string;
}): void => {
  if (!items?.length) {
    return;
  }

  const itemsCombined = items
    .map(item => translateInfoItem(item, tFunction))
    .join(", ");
  Log.warnAndToastDetailed(tFunction(headerTranslationKey), itemsCombined);
};

export const warnAndToastTranslatableItem = ({
  translatableItem,
  tFunction,
  debugInfo,
}: {
  translatableItem: ITranslatableItem;
  tFunction: TFunction;
  debugInfo?: string;
}): void => {
  if (!translatableItem) {
    Log.warnAndToast(errors.common.technicalProblemOccurred);
  }

  const translationKey =
    translatableItem.translationKey || errors.common.technicalProblemOccurred;
  const warningText = tFunction(
    translationKey,
    localizePermissionTranslationParams(
      translationKey,
      translatableItem.translationParams,
      tFunction,
    ),
  );
  Log.warnAndToast(warningText, debugInfo);
};
